<template>
  <div class="paint-wrap">
    <!-- Left Panel: Template & Guide -->
    <div class="panel template-panel">
      <div class="panel-header">
        <h2>بوم نقاشی</h2>
        <button class="back-btn" @click="goBack">بازگشت</button>
      </div>
      <div class="template-card">
        <div class="template-visual">{{ animalEmoji }}</div>
        <div class="template-info">
          <div class="template-title">الگو: {{ animalTitle }}</div>
          <p class="template-text">لطفاً برای من این را بساز: {{ animalTitle }}</p>
          
          <div class="template-actions">
            <!-- Button is hidden if no sound exists for this animal -->
            <button v-if="hasSoundEffect" class="tool-btn sound-btn" @click="playAudio">
              پخش صدای حیوان 🔊
            </button>
            
            <!-- TTS button to read the text -->
            <button class="tool-btn tts-btn" @click="speakPrompt" title="خواندن متن">
              🗣️
            </button>
          </div>
        </div>
      </div>
      <div class="note">نمونه فقط راهنماست و با فضای نقاشی متفاوت است.</div>
    </div>

    <!-- Right Panel: Canvas & Tools -->
    <div class="canvas-card">
      <div class="color-panel">
        <button v-for="c in palette" :key="c" class="color-swatch" :style="{ background: c }" @click="color=c" :aria-label="`انتخاب رنگ ${c}`"></button>
        <input type="color" v-model="color" class="color-picker" aria-label="انتخاب رنگ" />
        <label class="size-label">اندازه قلم</label>
        <input type="range" min="4" max="36" v-model="size" class="size-slider" />
      </div>
      <div class="tools">
        <button class="tool-btn" @click="mode='draw'" :class="{ active: mode==='draw' }">✏️ نقاشی</button>
        <button class="tool-btn" @click="mode='erase'" :class="{ active: mode==='erase' }">🧽 پاک‌کن</button>
        <button class="tool-btn" @click="mode='shape-circle'" :class="{ active: mode==='shape-circle' }">⚪ دایره</button>
        <button class="tool-btn" @click="mode='shape-square'" :class="{ active: mode==='shape-square' }">◻️ مربع</button>
        <button class="tool-btn" @click="mode='shape-triangle'" :class="{ active: mode==='shape-triangle' }">🔺 مثلث</button>
        <button class="tool-btn" @click="clearCanvas">🗑️ پاک‌کردن همه</button>
        <button class="primary submit-btn" @click="openFinishMenu">پایان نقاشی ✨</button>
      </div>
      <canvas ref="canvas" class="canvas"></canvas>
    </div>

    <!-- Magic / Finish Modal -->
    <div v-if="showMagicModal" class="modal-overlay">
      <div class="modal-content magic-theme">
        
        <!-- Generating State (Magic Loading) -->
        <div v-if="isMagicLoading" class="magic-loading">
          <div class="magic-spinner">
            <div class="wand">🪄</div>
            <div class="sparkles">✨</div>
          </div>
          <h3>در حال انجام جادو... 🧞‍♂️</h3>
          <p>پویش داره نقاشی تو رو به <strong>{{ magicText || 'یک جای جادویی' }}</strong> می‌بره!</p>
          <div class="progress-bar-magic"><div class="fill"></div></div>
        </div>

        <!-- Input State -->
        <div v-else>
          <div class="magic-icon">🧞‍♂️</div>
          <h3>نقاشی قشنگی کشیدی!</h3>
          <p>دوست داری با جادو واقعی‌ش کنم؟</p>
          
          <div class="preview-box">
            <img :src="canvasPreview" class="mini-preview" />
          </div>

          <div class="prompt-section">
            <label>به من بگو این {{ animalTitle }} کجاست؟</label>
            <div class="input-wrap">
              <input v-model="magicText" type="text" placeholder="مثلاً: توی فضا، زیر دریا..." @keyup.enter="confirmMagic" />
              <button class="mic-btn" @click="startDictation">🎤</button>
            </div>
          </div>

          <div class="actions-row">
            <button class="btn-magic" @click="confirmMagic">
              <span>✨ جادوی رنگ‌ها</span>
            </button>
            <button class="btn-simple" @click="confirmSimple">
              <span>فقط ذخیره کن</span>
            </button>
          </div>
          <button class="close-text" @click="showMagicModal=false">بازگشت به نقاشی</button>
        </div>
      </div>
    </div>

    <!-- Success Popup -->
    <div v-if="showPopup" class="popup-overlay">
      <div class="popup-content">
        <div class="popup-confetti">🎉</div>
        <h3>آفرین! تو {{ animalTitle }} رو کشیدی! 🌟</h3>
        <p v-if="magicRequestSent">نقاشی تو به <strong>دنیای جادویی</strong> رفت! بعداً توی گالری ببینش.</p>
        <p v-else>نقاشی‌ات ذخیره شد.</p>
        
        <div class="animal-grid">
          <button v-for="a in animals" :key="a" class="animal-btn" @click="selectNewAnimal(a)">
            <span class="emoji">{{ emojis[a] || '🎨' }}</span>
            <span class="name">{{ a }}</span>
          </button>
        </div>
        <button class="close-popup" @click="goBack">خروج</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useMutation } from '@vue/apollo-composable'
import { SUBMIT_PAINTING_MUTATION } from '../api/mutations'
import { useToast } from '../ui/toast'

const router = useRouter()
const route = useRoute()
const toast = useToast()
const roomSlug = route.params.slug

// UI States
const showPopup = ref(false)
const showMagicModal = ref(false)
const isMagicLoading = ref(false)
const magicRequestSent = ref(false)
const canvasPreview = ref('')
const magicText = ref('')

// Data
const ageGroup = ref('under7')
const topic = ref('')
const animalTitle = ref('')
const animalEmoji = ref('')

const animals = ['زرافه', 'گربه', 'سگ', 'خرگوش', 'فیل', 'شیر', 'اسب', 'ماهی', 'پروانه', 'پرنده', 'میمون', 'ماشین', 'خانه', 'درخت', 'گل']
const emojis = { 
  'زرافه': '🦒', 'گربه': '🐱', 'سگ': '🐶', 'خرگوش': '🐰', 'فیل': '🐘', 'شیر': '🦁',
  'اسب': '🐴', 'ماهی': '🐟', 'پروانه': '🦋', 'پرنده': '🐦', 'میمون': '🐵',
  'ماشین': '🚗', 'خانه': '🏠', 'درخت': '🌳', 'گل': '🌹'
}

// Sound Mapping (Real URLs)
const soundEffects = {
  'گربه': 'https://www.google.com/logos/fnbx/animal_sounds/cat.mp3',
  'سگ': 'https://www.google.com/logos/fnbx/animal_sounds/dog.mp3',
  'شیر': 'https://www.google.com/logos/fnbx/animal_sounds/lion.mp3',
  'فیل': 'https://www.google.com/logos/fnbx/animal_sounds/elephant.mp3',
  'اسب': 'https://www.google.com/logos/fnbx/animal_sounds/horse.mp3',
  'پرنده': 'https://www.google.com/logos/fnbx/animal_sounds/bird.mp3',
  'میمون': 'https://www.google.com/logos/fnbx/animal_sounds/ape.mp3',
  'گوسفند': 'https://www.google.com/logos/fnbx/animal_sounds/sheep.mp3',
  'ماهی': 'https://www.google.com/logos/fnbx/animal_sounds/whale.mp3', // Using whale for underwater sound
  'ماشین': 'https://quicksounds.com/uploads/tracks/285759972_1377076472_1143339024.mp3', // Car horn
  // Silent items
  'زرافه': null,
  'خرگوش': null, 
  'پروانه': null,
  'خانه': null,
  'درخت': null,
  'گل': null
}

const palette = ['#EF4444','#F59E0B','#FCD34D','#10B981','#3B82F6','#8B5CF6','#EC4899','#000000','#FFFFFF']
const color = ref('#3B82F6')
const size = ref(12)
const mode = ref('draw')
const canvas = ref(null)
let ctx
let isDrawing = false
let lastX = 0
let lastY = 0
const isSubmitting = ref(false)

const { mutate: submitMutate } = useMutation(SUBMIT_PAINTING_MUTATION)

// Computed Properties
const hasSoundEffect = computed(() => {
  return !!soundEffects[animalTitle.value]
})

// Methods
function goBack() {
  router.push(`/room/${roomSlug}`)
}

function pickRandomAnimal() {
  const idx = Math.floor(Math.random() * animals.length)
  const selected = animals[idx]
  
  // Prevent immediate repeat
  const last = localStorage.getItem(`lastAnimal:${roomSlug}`)
  if (selected === last && animals.length > 1) {
    pickRandomAnimal()
    return
  }
  
  animalTitle.value = selected
  animalEmoji.value = emojis[selected] || '🎨'
  topic.value = selected
  localStorage.setItem(`lastAnimal:${roomSlug}`, selected)
}

function playAudio() {
  const soundUrl = soundEffects[animalTitle.value]
  if (soundUrl) {
    const audio = new Audio(soundUrl)
    audio.play().catch(e => {
      console.error("Audio failed", e)
      toast?.show({ text: 'خطا در پخش صدا', type: 'error' })
    })
  }
}

function speakPrompt() {
  if (!('speechSynthesis' in window)) return
  window.speechSynthesis.cancel()
  const text = `این ${animalTitle.value} است. لطفاً آن را بکش.`
  const utter = new SpeechSynthesisUtterance(text)
  utter.lang = 'fa-IR'
  utter.rate = 0.9
  window.speechSynthesis.speak(utter)
}

// Canvas Logic
function clearCanvas() {
  if (!ctx) return
  ctx.clearRect(0, 0, canvas.value.width, canvas.value.height)
}

function onDown(e) {
  const { x, y } = getXY(e)
  if (mode.value.startsWith('shape-')) {
    drawShapeAt(x, y)
    isDrawing = false
    return
  }
  isDrawing = true
  lastX = x
  lastY = y
}

function onMove(e) {
  if (!isDrawing) return
  const { x, y } = getXY(e)
  ctx.strokeStyle = mode.value === 'erase' ? '#ffffff' : color.value
  ctx.lineWidth = Number(size.value)
  ctx.lineCap = 'round'
  ctx.beginPath()
  ctx.moveTo(lastX, lastY)
  ctx.lineTo(x, y)
  ctx.stroke()
  lastX = x
  lastY = y
}

function drawShapeAt(x, y) {
  const d = Number(size.value) * 3
  if (mode.value === 'shape-circle') {
    ctx.beginPath()
    ctx.fillStyle = color.value
    ctx.arc(x, y, d / 2, 0, Math.PI * 2)
    ctx.fill()
  } else if (mode.value === 'shape-square') {
    ctx.fillStyle = color.value
    ctx.fillRect(x - d / 2, y - d / 2, d, d)
  } else if (mode.value === 'shape-triangle') {
    ctx.beginPath()
    ctx.fillStyle = color.value
    ctx.moveTo(x, y - d / 2)
    ctx.lineTo(x - d / 2, y + d / 2)
    ctx.lineTo(x + d / 2, y + d / 2)
    ctx.closePath()
    ctx.fill()
  }
}

function onUp() { isDrawing = false }

function openFinishMenu() {
  if (!canvas.value) return
  canvasPreview.value = canvas.value.toDataURL('image/png')
  showMagicModal.value = true
  isMagicLoading.value = false 
  magicText.value = ''
}

function startDictation() {
  if (!('webkitSpeechRecognition' in window)) {
    toast?.show({ text: 'مرورگر شما پشتیبانی نمی‌کند', type: 'warning' })
    return
  }
  const recognition = new window.webkitSpeechRecognition()
  recognition.lang = 'fa-IR'
  recognition.onresult = (event) => {
    magicText.value = event.results[0][0].transcript
  }
  recognition.start()
}

async function confirmMagic() {
  isMagicLoading.value = true // Show loading UI
  magicRequestSent.value = true
  await submitPainting(true)
}

async function confirmSimple() {
  magicRequestSent.value = false
  await submitPainting(false)
}

async function submitPainting(isMagic) {
  if (isSubmitting.value) return
  isSubmitting.value = true
  
  try {
    const dataUrl = canvas.value.toDataURL('image/png')
    
    // Simulate slight network delay for better UX on magic
    if (isMagic) await new Promise(r => setTimeout(r, 1500))

    await submitMutate({ 
      data: { 
        roomSlug: roomSlug, 
        imageBase64: dataUrl, 
        topic: topic.value, 
        ageGroup: ageGroup.value, 
        stepsDone: [],
        magicRequested: isMagic,
        magicPrompt: magicText.value || null
      } 
    })
    
    showMagicModal.value = false
    showPopup.value = true
    isMagicLoading.value = false
  } catch (e) {
    toast?.show({ text: 'خطا در ارسال', type: 'error' })
    isMagicLoading.value = false
  } finally {
    isSubmitting.value = false
  }
}

function selectNewAnimal(anim) {
  animalTitle.value = anim
  animalEmoji.value = emojis[anim] || '🎨'
  topic.value = anim
  localStorage.setItem(`lastAnimal:${roomSlug}`, anim)
  clearCanvas()
  showPopup.value = false
}

// Setup
onMounted(() => {
  pickRandomAnimal()
  
  const c = canvas.value
  const desiredWidth = Math.min(window.innerWidth, 1000) - 60
  const desiredHeight = Math.min(window.innerHeight, 700) - 160
  c.style.width = desiredWidth + 'px'
  c.style.height = desiredHeight + 'px'
  const dpr = window.devicePixelRatio || 1
  c.width = Math.floor(desiredWidth * dpr)
  c.height = Math.floor(desiredHeight * dpr)
  ctx = c.getContext('2d')
  if (dpr && dpr !== 1) ctx.scale(dpr, dpr)
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, c.width, c.height)
  
  c.addEventListener('mousedown', onDown)
  c.addEventListener('mousemove', onMove)
  window.addEventListener('mouseup', onUp)
  c.addEventListener('touchstart', onDown, { passive: true })
  c.addEventListener('touchmove', onMove, { passive: true })
  window.addEventListener('touchend', onUp)
})

function getXY(e) {
  const r = canvas.value.getBoundingClientRect()
  const clientX = e.touches ? e.touches[0].clientX : e.clientX
  const clientY = e.touches ? e.touches[0].clientY : e.clientY
  const x = clientX - r.left
  const y = clientY - r.top
  return { x, y }
}
</script>

<script>
export default { name: 'PaintView' }
</script>

<style scoped>
.paint-wrap { display:grid; grid-template-columns: 420px 1fr; gap: 16px; padding: 24px; max-width: 1400px; margin: 0 auto; }
@media (max-width: 900px) {
  .paint-wrap { grid-template-columns: 1fr; padding: 12px; gap: 12px; }
  .template-visual { width: 80px; height: 80px; font-size: 48px; }
  .color-panel { grid-template-columns: repeat(auto-fill, 40px); justify-content: center; }
}

.panel { background: var(--card); border:1px solid var(--border); border-radius:16px; padding:16px; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1); }
.template-panel { background: #fff; }
.template-card { display:flex; gap:12px; align-items:center; background:#f0f9ff; border:2px dashed #bae6fd; border-radius:14px; padding:12px; margin-bottom: 8px; }
.template-visual { width:120px; height:120px; display:flex; align-items:center; justify-content:center; font-size:72px; background:#fff; border-radius:16px; flex-shrink: 0; box-shadow: 0 4px 12px rgba(0,0,0,0.05); }
.template-info { display:flex; flex-direction:column; gap:6px; }
.template-title { font-weight:800; color:#0369a1; font-size: 1.1rem; }
.template-text { color:#334155; font-size:14px; }
.template-actions { display:flex; gap:8px; }
.note { font-size:12px; color:#94a3b8; }

.canvas-card { background: var(--card); border:1px solid var(--border); border-radius:16px; padding:16px; overflow: hidden; display: flex; flex-direction: column; height: calc(100vh - 48px); max-height: 800px; box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1); }
.color-panel { display:flex; flex-wrap: wrap; gap: 10px; align-items:center; margin-bottom:12px; padding-bottom: 12px; border-bottom: 1px solid #f1f5f9; }
.color-swatch { width:36px; height:36px; border-radius:50%; border:2px solid #fff; box-shadow:0 0 0 2px #e2e8f0; cursor:pointer; transition: transform 0.2s; }
.color-swatch:hover { transform: scale(1.1); }
.color-picker { width:36px; height:36px; border:none; background:transparent; cursor: pointer; }
.size-label { font-size:13px; font-weight: 500; color:#64748b; margin-right: 8px; }
.size-slider { width: 120px; accent-color: var(--accent); }

.tools { display:flex; align-items:center; gap:8px; margin-bottom:12px; flex-wrap: wrap; }
.tool-btn { background:#f1f5f9; color:#475569; border:none; border-radius:10px; padding:8px 16px; font-weight:600; font-size:14px; cursor:pointer; display: flex; align-items: center; gap: 6px; transition: all 0.2s; }
.tool-btn:hover { background:#e2e8f0; }
.tool-btn.active { background:#eff6ff; color:#2563eb; }
.sound-btn { background: #dbeafe; color: #1e40af; }
.sound-btn:hover { background: #bfdbfe; }
.tts-btn { width: 40px; justify-content: center; padding: 8px; }

.submit-btn { margin-right: auto; background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%); color: white; padding: 10px 24px; border-radius: 12px; box-shadow: 0 4px 12px rgba(37, 99, 235, 0.3); transition: all 0.3s; }
.submit-btn:hover { transform: translateY(-2px); box-shadow: 0 6px 16px rgba(37, 99, 235, 0.4); }

.canvas { flex: 1; width:100%; border:2px dashed #e2e8f0; border-radius:12px; touch-action: none; background:#fff; cursor: crosshair; }

/* Modals */
.modal-overlay, .popup-overlay { position:fixed; inset:0; background:rgba(15, 23, 42, 0.6); display:flex; align-items:center; justify-content:center; z-index:2000; backdrop-filter: blur(4px); }
.modal-content.magic-theme { background: linear-gradient(135deg, #fff 0%, #f0f9ff 100%); border: 4px solid #bae6fd; box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25); border-radius: 24px; padding: 32px; text-align: center; width: 90%; max-width: 480px; position: relative; min-height: 400px; display: flex; flex-direction: column; justify-content: center; }
.magic-icon { font-size: 48px; margin-bottom: 16px; animation: bounce 2s infinite; }
@keyframes bounce { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
.modal-content h3 { color: #0c4a6e; margin-bottom: 8px; font-size: 24px; }
.modal-content p { color: #0284c7; margin-bottom: 24px; font-size: 16px; }
.preview-box { background: #fff; padding: 12px; border-radius: 12px; display: inline-block; margin-bottom: 24px; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1); transform: rotate(-2deg); }
.mini-preview { max-width: 150px; border-radius: 8px; border: 1px solid #e2e8f0; }
.prompt-section { text-align: right; margin-bottom: 24px; width: 100%; }
.prompt-section label { display: block; font-weight: 600; color: #334155; margin-bottom: 8px; font-size: 14px; }
.input-wrap { display: flex; gap: 8px; }
.input-wrap input { flex: 1; padding: 10px 14px; border: 2px solid #e2e8f0; border-radius: 12px; font-size: 14px; outline: none; transition: border 0.2s; }
.input-wrap input:focus { border-color: #3b82f6; }
.mic-btn { background: #f1f5f9; border: none; width: 42px; width: 44px; border-radius: 12px; font-size: 20px; cursor: pointer; color: #64748b; }
.mic-btn:hover { background: #e2e8f0; color: #ef4444; }

.actions-row { display: flex; flex-direction: column; gap: 12px; margin-bottom: 16px; width: 100%; }
.btn-magic { background: linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%); color: white; padding: 14px; border: none; border-radius: 14px; font-weight: 700; font-size: 16px; cursor: pointer; transition: transform 0.2s; display: flex; justify-content: center; align-items: center; gap: 8px; box-shadow: 0 4px 12px rgba(139, 92, 246, 0.4); }
.btn-magic:hover { transform: scale(1.02); }
.btn-simple { background: transparent; color: #64748b; padding: 12px; border: 2px solid #e2e8f0; border-radius: 14px; font-weight: 600; cursor: pointer; transition: all 0.2s; }
.btn-simple:hover { background: #f8fafc; color: #334155; border-color: #cbd5e1; }
.close-text { background: none; border: none; color: #94a3b8; font-size: 13px; cursor: pointer; text-decoration: underline; }

/* Magic Loading Styles */
.magic-loading { display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100%; }
.magic-spinner { font-size: 60px; margin-bottom: 20px; position: relative; animation: float 3s ease-in-out infinite; }
.wand { position: relative; z-index: 2; animation: wave 1.5s ease-in-out infinite; }
.sparkles { position: absolute; top: -10px; right: -20px; font-size: 40px; animation: sparkle 1s linear infinite; }
.progress-bar-magic { width: 100%; height: 8px; background: #e0f2fe; border-radius: 99px; overflow: hidden; margin-top: 20px; }
.progress-bar-magic .fill { height: 100%; width: 50%; background: linear-gradient(90deg, #8b5cf6, #ec4899); animation: progress-indeterminate 2s infinite linear; }

@keyframes wave { 0%, 100% { transform: rotate(0deg); } 50% { transform: rotate(-20deg); } }
@keyframes sparkle { 0% { opacity: 0; transform: scale(0.5); } 50% { opacity: 1; transform: scale(1.2); } 100% { opacity: 0; transform: scale(0.5); } }
@keyframes float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
@keyframes progress-indeterminate { 0% { transform: translateX(-100%); } 100% { transform: translateX(200%); } }

.popup-content { background:#fff; border-radius:24px; padding:32px; text-align:center; max-width:600px; width:90%; box-shadow: 0 20px 50px rgba(0,0,0,0.3); animation: pop-in 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275); }
@keyframes pop-in { from { transform: scale(0.8); opacity:0; } to { transform: scale(1); opacity:1; } }
.popup-confetti { font-size: 64px; margin-bottom: 16px; animation: spin 2s infinite linear; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
.popup-content h3 { font-size: 28px; color: #1f57c9; margin-bottom: 8px; }
.popup-content p { color: #4b5563; margin-bottom: 24px; font-size: 18px; }
.animal-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(80px, 1fr)); gap: 12px; margin-bottom: 24px; max-height: 300px; overflow-y: auto; }
.animal-btn { display: flex; flex-direction: column; align-items: center; gap: 6px; background: #f3f4f6; border: 2px solid transparent; border-radius: 16px; padding: 12px; cursor: pointer; transition: all 0.2s; }
.animal-btn:hover { background: #e0e7ff; transform: translateY(-2px); }
.animal-btn .emoji { font-size: 32px; }
.animal-btn .name { font-size: 14px; font-weight: 700; color: #374151; }
.close-popup { background: #ef4444; color: white; border: none; padding: 10px 24px; border-radius: 12px; font-weight: bold; cursor: pointer; font-size: 16px; }
.panel-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; }
.back-btn { background: #e5e7eb; border: none; padding: 6px 12px; border-radius: 8px; cursor: pointer; font-weight: 600; color: #374151; }
.back-btn:hover { background: #d1d5db; }
</style>